import { z } from 'zod';
import { AppError } from '../errors';
import { type LocalDate, type LocalTime, localToUtc } from '../time';
import type { MeetingCalendarInfo } from './calendar';
import { emailSchema, idSchema, localDateSchema, localTimeSchema, markdownSchema, nameSchema } from './common';

/**
 * Spotkania (docs/3VTOS_MVP.md §8, Figma 05 „Spotkanie z propozycjami zadań”): `meetings`
 * (packages/db/src/schema/meetings.ts). Moduł uprawnień: `clients` (DECISIONS: spotkania i oferty należą do Klientów).
 *
 * Spotkanie: tytuł, data, klient lub projekt, uczestnicy, notatki i wklejona transkrypcja tekstowa. AI (fake bez klucza)
 * przygotowuje podsumowanie, ustalenia, pytania i propozycje zadań (`task_proposals`, `source_type = 'meeting'`,
 * `dedup_key` = brak duplikatów przy ponownym przetworzeniu); właściciel je przegląda. Transkrypcja to dane klienta,
 * nie polecenia (Context Builder opakowuje ją `wrapUntrustedData`).
 * Wejścia i wyjścia usług dopisuje agent modułu meetings.
 */

export const MEETING_AI_STATUSES = ['none', 'queued', 'done', 'failed'] as const;
export type MeetingAiStatus = (typeof MEETING_AI_STATUSES)[number];
export const meetingAiStatusSchema = z.enum(MEETING_AI_STATUSES);

/** Uczestnik: osoba z zespołu (`userId`), kontakt klienta (`contactId`) albo sam opis (imię, email). */
export const meetingParticipantSchema = z.object({
  name: nameSchema,
  email: emailSchema.nullable().optional(),
  userId: idSchema.nullable().optional(),
  contactId: idSchema.nullable().optional(),
});
export type MeetingParticipant = z.infer<typeof meetingParticipantSchema>;

/** Wynik przetworzenia transkrypcji (poza podsumowaniem w `summary_md` i propozycjami w `task_proposals`). */
export interface MeetingAiOutput {
  /**
   * „Ustalenia” zaproponowane przez AI (wszystkie przetworzenia, bez powtórek). To PROPOZYCJE: do Wiedzy trafiają
   * dopiero po decyzji osoby (`meetings.decideDecisions`, MVP §8, §10, §11).
   */
  decisions: string[];
  /** klucze (`meetingDecisionKey`) ustaleń odrzuconych przez osobę — ponowne przetworzenie ich nie przywraca */
  rejectedDecisions?: string[];
  /** otwarte pytania */
  questions: string[];
  /** symulacja (adapter fake) */
  simulated?: boolean;
  /** czytelny błąd ostatniego przetworzenia (`ai_status = 'failed'`); poprzedni wynik zostaje */
  error?: string | null;
  /** sha256 transkrypcji i notatek z chwili przetworzenia (zmiana = wynik nieaktualny) */
  sourceHash?: string | null;
}
export const meetingAiOutputSchema = z.object({
  decisions: z.array(z.string().max(1000)).max(100).default([]),
  rejectedDecisions: z.array(z.string().max(200)).max(200).optional(),
  questions: z.array(z.string().max(1000)).max(50).default([]),
  simulated: z.boolean().optional(),
  error: z.string().max(1000).nullable().optional(),
  sourceHash: z.string().max(128).nullable().optional(),
});

/** Transkrypcja tekstowa (wklejona albo z pliku .txt / .vtt), do 300 tys. znaków. */
export const MAX_TRANSCRIPT_CHARS = 300_000;
export const transcriptSchema = z
  .string()
  .max(MAX_TRANSCRIPT_CHARS, { message: 'Transkrypcja jest za długa (najwyżej 300 tys. znaków)' });

/** Czas spotkania w Europe/Warsaw: data, początek, koniec opcjonalny (tego samego dnia). */
export const meetingTimingShape = {
  date: localDateSchema,
  start: localTimeSchema,
  end: localTimeSchema.nullable().optional(),
};

export function resolveMeetingTiming(input: { date: LocalDate; start: LocalTime; end?: LocalTime | null }): {
  startsAt: Date;
  endsAt: Date | null;
} {
  const startsAt = localToUtc(input.date, input.start);
  const endsAt = input.end ? localToUtc(input.date, input.end) : null;
  if (endsAt && endsAt.getTime() <= startsAt.getTime()) {
    throw new AppError('VALIDATION_ERROR', 'Koniec spotkania musi być po początku.', {
      details: { issues: [{ path: 'end', message: 'Koniec musi być po początku' }] },
    });
  }
  return { startsAt, endsAt };
}

export const meetingFieldsShape = {
  title: z.string().trim().min(1, { message: 'Tytuł jest wymagany' }).max(300, { message: 'Najwyżej 300 znaków' }),
  ...meetingTimingShape,
  clientId: idSchema.nullable().optional(),
  projectId: idSchema.nullable().optional(),
  participants: z.array(meetingParticipantSchema).max(50).default([]),
  /** miejsce albo narzędzie, np. „Google Meet” */
  location: z.string().trim().max(300).nullable().optional(),
  notesMd: markdownSchema.nullable().optional(),
  transcriptText: transcriptSchema.nullable().optional(),
};
export const meetingFieldsSchema = z.object(meetingFieldsShape).superRefine((v, ctx) => {
  if (v.end && v.end <= v.start) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['end'], message: 'Koniec musi być po początku' });
  }
});
export type MeetingFields = z.infer<typeof meetingFieldsSchema>;

/** Klucz deduplikacji propozycji zadania ze spotkania (ponowne przetworzenie nie tworzy duplikatów). */
export function meetingProposalDedupKey(meetingId: string, title: string): string {
  const norm = title
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/ł/g, 'l')
    .replace(/Ł/g, 'L')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .slice(0, 120);
  return `meeting:${meetingId}:${norm}`;
}

/** Tekst ustalenia sprowadzony do porównania (ponowne przetworzenie nie dubluje decyzji w Wiedzy). */
export function meetingDecisionKey(text: string): string {
  return meetingProposalDedupKey('', text).slice('meeting::'.length);
}

// ── Podobne brzmienie (ponowne przetworzenie przez model, który przeformułowuje tytuły) ──

const SIMILARITY_STOPWORDS = new Set([
  'a',
  'albo',
  'bez',
  'by',
  'co',
  'czy',
  'dla',
  'do',
  'i',
  'jak',
  'jest',
  'juz',
  'lub',
  'na',
  'nad',
  'nie',
  'o',
  'od',
  'oraz',
  'po',
  'pod',
  'przez',
  'przy',
  'sa',
  'sie',
  'ta',
  'te',
  'ten',
  'to',
  'tez',
  'u',
  'w',
  'we',
  'z',
  'za',
  'ze',
]);
const NUMBER_WORDS: Record<string, string> = {
  jeden: '1',
  jedna: '1',
  jedno: '1',
  jednego: '1',
  jednej: '1',
  dwa: '2',
  dwie: '2',
  dwoch: '2',
  dwu: '2',
  dwoma: '2',
  trzy: '3',
  trzech: '3',
  cztery: '4',
  czterech: '4',
  piec: '5',
  pieciu: '5',
  szesc: '6',
  siedem: '7',
  osiem: '8',
  dziewiec: '9',
  dziesiec: '10',
};

/**
 * Rdzenie słów tytułu: bez wielkości liter i ogonków, bez słów pomocniczych („do”, „na”…), liczebniki jako cyfry
 * („dwóch” → „2”), słowa skrócone do 4 liter (odmiana: „rejestrację” / „rejestracji” → „reje”).
 */
export function meetingTextStems(text: string): Set<string> {
  const words = meetingDecisionKey(text).split(' ').filter(Boolean);
  const out = new Set<string>();
  for (const w of words) {
    if (SIMILARITY_STOPWORDS.has(w)) continue;
    out.add(NUMBER_WORDS[w] ?? (/^\d+$/.test(w) ? w : w.slice(0, 4)));
  }
  return out;
}

/**
 * To samo zadanie / ustalenie w innym brzmieniu (backstop przy ponownym przetworzeniu): wszystkie rdzenie krótszego
 * tytułu są w dłuższym i wspólne są co najmniej 2 (inny szyk, odmiana, liczebnik słownie, dodany czasownik:
 * „Makiety mobile” ≈ „Przygotować makiety mobilne”). Celowo ostrożnie — różne zadania o podobnych słowach
 * („ofertę na hosting” / „fakturę za hosting”) zostają osobno, a zmiany słów (synonimy) rozpoznaje model, który
 * dostaje listę istniejących propozycji i ustaleń.
 */
export function isSameMeetingItem(a: string, b: string): boolean {
  if (meetingDecisionKey(a) === meetingDecisionKey(b)) return true;
  const sa = meetingTextStems(a);
  const sb = meetingTextStems(b);
  const [small, big] = sa.size <= sb.size ? [sa, sb] : [sb, sa];
  if (small.size < 2) return false;
  for (const s of small) if (!big.has(s)) return false;
  return true;
}

// ── Usługi modułu meetings (Fala 2): wejścia i kształty odpowiedzi ──────────

/** Typ akceptacji: propozycje zadań ze spotkania (lista w Akceptacjach; przegląd w widoku spotkania). */
export const MEETING_TASK_PROPOSALS_APPROVAL = 'work.task_proposals';
export const meetingTaskProposalsPayloadSchema = z.object({
  meetingId: idSchema,
  proposalIds: z.array(idSchema).min(1).max(100),
});
export type MeetingTaskProposalsPayload = z.infer<typeof meetingTaskProposalsPayloadSchema>;

/** Kolejka przetwarzania transkrypcji (worker). */
export const MEETING_PROCESS_QUEUE = 'meetings.process';

// ── Zaproszenie do kalendarza osoby (DECISIONS 2026-10-09 [S] „Spotkania w Kalendarzu Google…”) ─────────────

/** Kolejka: mail z zaproszeniem kalendarzowym (text/calendar) z poczty systemowej do autora spotkania. */
export const MEETING_INVITE_QUEUE = 'meetings.invite';

/**
 * Dane zadania `meetings.invite` (migawka spotkania z chwili zmiany; `version` = wersja rekordu po zmianie,
 * SEQUENCE = version − 1). Jedno zadanie na wersję (`singletonKey` = `invite:<id>:<version>`).
 */
export const meetingInviteJobSchema = z.object({
  meetingId: idSchema,
  version: z.number().int().positive(),
  method: z.enum(['REQUEST', 'CANCEL']),
  /** 'created' | 'updated' | 'cancelled' — temat maila */
  kind: z.enum(['created', 'updated', 'cancelled']),
  title: z.string().max(300),
  startsAt: z.string().datetime(),
  endsAt: z.string().datetime().nullable(),
  location: z.string().max(300).nullable(),
});
export type MeetingInviteJob = z.infer<typeof meetingInviteJobSchema>;

/** Ustawienia osoby (Ustawienia → Profil → Kalendarz); `user_settings`, wspólne dla workspace'ów osoby. */
export const meetingInviteSettingsSchema = z.object({
  /** „Wysyłaj zaproszenia do mojego kalendarza” (domyślnie włączone) */
  enabled: z.boolean(),
  /** „Adres kalendarza” (np. Gmail); null = email konta */
  calendarEmail: emailSchema.nullable(),
});
export type MeetingInviteSettings = z.infer<typeof meetingInviteSettingsSchema> & {
  /** email konta (domyślny adres zaproszeń) */
  accountEmail: string;
  /**
   * Adres kalendarza potwierdzony kodem z maila (null adresu = email konta = potwierdzony). Dopóki nie, zaproszenia
   * idą na email konta (DECISIONS 2026-10-09 [T] „Adres kalendarza potwierdzany kodem”).
   */
  calendarEmailVerified: boolean;
  /** wysłany kod potwierdzenia adresu kalendarza (bez kodu); null = brak */
  pendingCode: { expiresAt: string; resendAvailableAt: string } | null;
};

/** Kod potwierdzenia adresu kalendarza: 6 cyfr, ważny 15 min, 5 prób, ponowny po 60 s, najwyżej 5 maili na dobę. */
export const CALENDAR_EMAIL_CODE = {
  digits: 6,
  ttlMinutes: 15,
  maxAttempts: 5,
  resendSeconds: 60,
  maxPerDay: 5,
} as const;

export const confirmCalendarEmailInput = z
  .object({
    code: z
      .string()
      .trim()
      .regex(/^\d{6}$/, 'Kod ma 6 cyfr'),
  })
  .strict();

/** Wynik potwierdzenia: błędny kod nie jest wyjątkiem (licznik prób musi się zapisać). */
export interface ConfirmCalendarEmailResult {
  confirmed: boolean;
  reason?: 'wrong_code' | 'expired' | 'too_many_attempts' | 'no_code';
  settings: MeetingInviteSettings;
}

export const updateMeetingInviteSettingsInput = z
  .object({
    enabled: z.boolean().optional(),
    /** pusty tekst albo null = email konta */
    calendarEmail: z
      .union([emailSchema, z.literal('').transform(() => null)])
      .nullable()
      .optional(),
  })
  .strict();
/** Po tylu minutach w kolejce można zlecić przetworzenie ponownie (worker mógł zostać zatrzymany). */
export const MEETING_AI_STUCK_AFTER_MIN = 15;

/** Komunikaty błędów modułu (PL). */
export const MEETING_MESSAGES = {
  archived: 'Spotkanie jest w archiwum.',
  archivedClient: 'Klient jest w archiwum.',
  archivedProject: 'Projekt jest w archiwum.',
  projectOtherClient: 'Projekt należy do innego klienta.',
  nothingToProcess: 'Dodaj transkrypcję albo notatki, żeby AI mogło je przetworzyć.',
  alreadyQueued: 'AI już pracuje nad tym spotkaniem.',
  aiNeedsPerson: 'AI działa zawsze w imieniu osoby.',
  participantNotMember: 'Ta osoba nie należy do zespołu.',
  participantContactOtherClient: 'Kontakt należy do innego klienta.',
  participantContactNoClient: 'Najpierw wybierz klienta spotkania.',
  proposalDecided: 'Propozycja jest już rozstrzygnięta.',
  proposalsDecidedInMeeting:
    'Propozycje rozstrzygnięte w widoku spotkania przez osobę bez uprawnienia do zatwierdzania akcji AI.',
  proposalOtherMeeting: 'Propozycja nie pochodzi z tego spotkania.',
  editNotSelected: 'Zmiany dotyczą propozycji, której nie wybrano.',
  duplicateIds: 'Ta sama propozycja podana dwa razy.',
  decisionNotFound: 'Tego ustalenia nie ma w wyniku AI. Odśwież widok spotkania.',
  decisionChoiceEmpty: 'Wybierz ustalenia do zapisania albo odrzucenia.',
  decisionBothWays: 'To samo ustalenie nie może być jednocześnie zapisane i odrzucone.',
} as const;

export const MEETING_LIST_WHEN = ['upcoming', 'past', 'all'] as const;
export type MeetingListWhen = (typeof MEETING_LIST_WHEN)[number];

export const listMeetingsInput = z
  .object({
    /** nadchodzące (od najbliższego; trwające też), przeszłe (od najnowszego) albo wszystkie (od najnowszego) */
    when: z.enum(MEETING_LIST_WHEN).default('all'),
    clientId: idSchema.optional(),
    projectId: idSchema.optional(),
    q: z.string().trim().max(200).optional(),
    archived: z.boolean().default(false),
    limit: z.number().int().min(1).max(200).default(50),
  })
  .strict();
export type ListMeetingsInput = z.input<typeof listMeetingsInput>;

export const meetingIdInput = z.object({ id: idSchema }).strict();

function endAfterStart(v: { start?: LocalTime; end?: LocalTime | null }, ctx: z.RefinementCtx): void {
  if (v.start && v.end && v.end <= v.start) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['end'], message: 'Koniec musi być po początku' });
  }
}

export const createMeetingInput = z.object(meetingFieldsShape).strict().superRefine(endAfterStart);
export type CreateMeetingInput = z.input<typeof createMeetingInput>;

/** Edycja: tylko podane pola. Zmiana daty / godzin liczona razem z bieżącymi wartościami (Europe/Warsaw). */
export const updateMeetingInput = z
  .object({
    id: idSchema,
    version: z.number().int().positive(),
    title: meetingFieldsShape.title.optional(),
    date: localDateSchema.optional(),
    start: localTimeSchema.optional(),
    end: localTimeSchema.nullable().optional(),
    clientId: idSchema.nullable().optional(),
    projectId: idSchema.nullable().optional(),
    participants: z.array(meetingParticipantSchema).max(50).optional(),
    location: z.string().trim().max(300).nullable().optional(),
    notesMd: markdownSchema.nullable().optional(),
    transcriptText: transcriptSchema.nullable().optional(),
  })
  .strict()
  .superRefine(endAfterStart);
export type UpdateMeetingInput = z.input<typeof updateMeetingInput>;

export const archiveMeetingInput = z.object({ id: idSchema, version: z.number().int().positive().optional() }).strict();
export const processMeetingInput = z.object({ id: idSchema, version: z.number().int().positive().optional() }).strict();

export const nextMeetingInput = z
  .object({
    clientId: idSchema.optional(),
    projectId: idSchema.optional(),
    /** „Moje”: spotkania, w których osoba jest uczestnikiem albo które dodała */
    mine: z.boolean().default(false),
  })
  .strict();
export type NextMeetingInput = z.input<typeof nextMeetingInput>;

export const meetingFormOptionsInput = z.object({ clientId: idSchema.nullable().optional() }).strict();

const proposalTitleSchema = z
  .string()
  .trim()
  .min(1, { message: 'Tytuł jest wymagany' })
  .max(300, { message: 'Najwyżej 300 znaków' });

/** Zmiany propozycji przed przyjęciem (tytuł, wykonawca, termin). */
export const meetingProposalEditSchema = z
  .object({
    id: idSchema,
    version: z.number().int().positive().optional(),
    title: proposalTitleSchema.optional(),
    assigneeUserId: idSchema.nullable().optional(),
    dueDate: localDateSchema.nullable().optional(),
  })
  .strict();
export type MeetingProposalEdit = z.infer<typeof meetingProposalEditSchema>;

function uniqueIds(ids: readonly string[], ctx: z.RefinementCtx, path: string): void {
  if (new Set(ids).size !== ids.length) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message: MEETING_MESSAGES.duplicateIds });
  }
}

/** Przyjęcie wybranych propozycji: zadania przez `createTaskFromSource` (pochodzenie: spotkanie). */
export const acceptMeetingProposalsInput = z
  .object({
    meetingId: idSchema,
    ids: z.array(idSchema).min(1).max(50),
    edits: z.array(meetingProposalEditSchema).max(50).optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    uniqueIds(v.ids, ctx, 'ids');
    const edits = v.edits ?? [];
    uniqueIds(
      edits.map((e) => e.id),
      ctx,
      'edits',
    );
    edits.forEach((e, i) => {
      if (!v.ids.includes(e.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['edits', i, 'id'],
          message: MEETING_MESSAGES.editNotSelected,
        });
      }
    });
  });
export type AcceptMeetingProposalsInput = z.input<typeof acceptMeetingProposalsInput>;

export const rejectMeetingProposalsInput = z
  .object({ meetingId: idSchema, ids: z.array(idSchema).min(1).max(50) })
  .strict()
  .superRefine((v, ctx) => uniqueIds(v.ids, ctx, 'ids'));
export type RejectMeetingProposalsInput = z.input<typeof rejectMeetingProposalsInput>;

// ── Kształty odpowiedzi ─────────────────────────────────────────────────────
export interface MeetingClientRef {
  id: string;
  name: string;
  color: string | null;
}

export interface MeetingProjectRef {
  id: string;
  name: string;
}

export interface MeetingPersonRef {
  userId: string;
  name: string;
  initials: string | null;
}

export interface MeetingListItem {
  id: string;
  version: number;
  title: string;
  /** ISO (UTC) */
  startsAt: string;
  endsAt: string | null;
  /** data i godziny w Europe/Warsaw */
  date: LocalDate;
  start: LocalTime;
  end: LocalTime | null;
  client: MeetingClientRef | null;
  project: MeetingProjectRef | null;
  participants: MeetingParticipant[];
  location: string | null;
  hasTranscript: boolean;
  aiStatus: MeetingAiStatus;
  /** propozycje zadań czekające na decyzję */
  pendingProposals: number;
  archivedAt: string | null;
  /** spotkanie z kalendarza (link iCal, Fala 9): prywatność kalendarza osobistego; null = dodane w aplikacji */
  calendar?: MeetingCalendarInfo | null;
}

export interface MeetingListResult {
  items: MeetingListItem[];
  truncated: boolean;
}

export interface MeetingProposalDto {
  id: string;
  version: number;
  title: string;
  description: string | null;
  status: 'proposed' | 'accepted' | 'rejected';
  assignee: MeetingPersonRef | null;
  dueDate: LocalDate | null;
  /** zadanie utworzone z propozycji */
  task: { id: string; number: number; title: string } | null;
  decidedAt: string | null;
}

/** Ustalenie zapisane w Wiedzy (rodzaj „decyzja”, źródło: to spotkanie). */
export interface MeetingSavedDecision {
  id: string;
  title: string;
  decidedOn: LocalDate | null;
}

export const MEETING_DECISION_STATUSES = ['proposed', 'saved', 'rejected'] as const;
export type MeetingDecisionStatus = (typeof MEETING_DECISION_STATUSES)[number];

/**
 * Ustalenie ze spotkania w widoku: propozycja AI do przeglądu (`proposed`), zapisana w Wiedzy po decyzji osoby
 * (`saved`, `knowledgeId` = rekord, gdy osoba go widzi) albo odrzucona (`rejected`).
 */
export interface MeetingDecisionItem {
  /** `meetingDecisionKey(text)` — identyfikator ustalenia w decyzji */
  key: string;
  text: string;
  status: MeetingDecisionStatus;
  knowledgeId: string | null;
}

/**
 * Decyzja o ustaleniach (klucze z `decisionItems`): zapis w Wiedzy jako decyzje ze źródłem = spotkanie albo
 * odrzucenie. Nic nie trafia do Wiedzy bez tej decyzji osoby.
 */
export const decideMeetingDecisionsInput = z
  .object({
    meetingId: idSchema,
    save: z.array(z.string().min(1).max(200)).max(50).default([]),
    reject: z.array(z.string().min(1).max(200)).max(50).default([]),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.save.length === 0 && v.reject.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['save'], message: MEETING_MESSAGES.decisionChoiceEmpty });
    }
    if (v.save.some((k) => v.reject.includes(k))) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['reject'], message: MEETING_MESSAGES.decisionBothWays });
    }
  });
export type DecideMeetingDecisionsInput = z.input<typeof decideMeetingDecisionsInput>;

export interface MeetingDecisionsResult {
  saved: { key: string; knowledgeId: string }[];
  rejected: string[];
}

export interface MeetingAiState {
  status: MeetingAiStatus;
  /** błąd ostatniego przetworzenia (czytelny, PL) */
  error: string | null;
  processedAt: string | null;
  /** wynik z adaptera symulacji (bez wysyłania danych do dostawcy AI) */
  simulated: boolean;
  /** transkrypcja albo notatki zmieniły się po przetworzeniu */
  stale: boolean;
  /** w kolejce dłużej niż `MEETING_AI_STUCK_AFTER_MIN` minut (można zlecić ponownie) */
  stuck: boolean;
}

export interface MeetingDetail extends MeetingListItem {
  notesMd: string | null;
  transcriptText: string | null;
  summaryMd: string | null;
  /** „Ustalenia” zaproponowane przez AI (teksty; status każdego w `decisionItems`) */
  decisions: string[];
  /** ustalenia ze statusem: do przeglądu, zapisane w Wiedzy, odrzucone */
  decisionItems: MeetingDecisionItem[];
  questions: string[];
  ai: MeetingAiState;
  proposals: MeetingProposalDto[];
  savedDecisions: MeetingSavedDecision[];
  /** zapis ustaleń w Wiedzy / odrzucenie: edycja spotkania i Wiedza = Edycja */
  canDecideDecisions: boolean;
  createdBy: MeetingPersonRef | null;
  /** czekająca akceptacja propozycji (lista w Akceptacjach) */
  approvalId: string | null;
  canEdit: boolean;
  canProcess: boolean;
  /** przyjęcie / odrzucenie propozycji: Zadania = Edycja i edycja rekordu spotkania */
  canDecideProposals: boolean;
}

export interface NextMeeting {
  id: string;
  title: string;
  startsAt: string;
  endsAt: string | null;
  date: LocalDate;
  start: LocalTime;
  end: LocalTime | null;
  clientId: string | null;
  clientName: string | null;
  clientColor: string | null;
  projectId: string | null;
  projectName: string | null;
  location: string | null;
}

export interface MeetingFormOptions {
  clients: MeetingClientRef[];
  projects: { id: string; name: string; clientId: string | null }[];
  members: MeetingPersonRef[];
  /** kontakty klienta z wejścia (pusto bez klienta albo bez dostępu do kontaktów) */
  contacts: { id: string; clientId: string; name: string; email: string | null }[];
}

export interface ProcessMeetingResult {
  id: string;
  version: number;
  aiStatus: MeetingAiStatus;
}

export interface MeetingProposalDecisionResult {
  accepted: { proposalId: string; taskId: string; taskNumber: number }[];
  rejected: string[];
  /** akceptacja rozstrzygnięta automatycznie (wszystkie propozycje z niej mają decyzję) */
  approval: { id: string; status: 'succeeded' | 'rejected' } | null;
}

// ── Wynik AI ────────────────────────────────────────────────────────────────

export interface MeetingAiTaskProposal {
  title: string;
  description: string | null;
  /** „Seban”, „Piotr” — wykonawca tylko, gdy pasuje dokładnie do jednej osoby z zespołu */
  assigneeHint: string | null;
  /** „do wtorku”, „7.10” — termin tylko, gdy jednoznaczny */
  dueHint: string | null;
}

/** Wynik strukturalny przetworzenia spotkania (kontrakt dla dostawców AI). */
export interface MeetingAiResult {
  summary: string;
  decisions: string[];
  questions: string[];
  taskProposals: MeetingAiTaskProposal[];
}

export const MEETING_AI_LIMITS = {
  summaryChars: 4000,
  itemChars: 1000,
  items: 30,
  proposals: 20,
  titleChars: 300,
} as const;

function clip(v: unknown, max: number): string {
  return typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().slice(0, max) : '';
}

function stringList(v: readonly unknown[], max: number, chars: number): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of v) {
    const text = clip(typeof item === 'string' ? item : (item as { text?: unknown } | null)?.text, chars);
    const key = meetingDecisionKey(text);
    if (!text || seen.has(key)) continue;
    seen.add(key);
    out.push(text);
    if (out.length >= max) break;
  }
  return out;
}

/** Termin w nawiasie na końcu tytułu: „(do wtorku)”, „(na 7.10)”, „(termin: piątek)”. */
const DUE_IN_TITLE = /\s*\(((?:do|na|termin:?|deadline:?)\s+[^()]{1,60})\)\s*$/i;

function parseJsonObject(text: string): unknown {
  const fenced = /`{3}(?:json)?\s*([\s\S]*?)`{3}/i.exec(text);
  const body = fenced?.[1] ?? text;
  const start = body.indexOf('{');
  const end = body.lastIndexOf('}');
  if (start < 0 || end <= start) return null;
  try {
    return JSON.parse(body.slice(start, end + 1));
  } catch {
    return null;
  }
}

/**
 * Wynik AI z dowolnego kształtu (obiekt albo JSON w tekście, także w bloku kodu json): pola przycięte do limitów,
 * puste pozycje i duplikaty pominięte. `null` = nie da się odczytać. Rozpoznaje też kształt `{ tasks: [{ title }] }`
 * (zadanie `extract_tasks` adaptera fake).
 */
export function coerceMeetingAiResult(raw: unknown): Partial<MeetingAiResult> | null {
  const value = typeof raw === 'string' ? parseJsonObject(raw.trim()) : raw;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const o = value as Record<string, unknown>;
  const out: Partial<MeetingAiResult> = {};
  if (typeof o.summary === 'string') out.summary = clip(o.summary, MEETING_AI_LIMITS.summaryChars);
  if (Array.isArray(o.decisions)) {
    out.decisions = stringList(o.decisions, MEETING_AI_LIMITS.items, MEETING_AI_LIMITS.itemChars);
  }
  if (Array.isArray(o.questions)) {
    out.questions = stringList(o.questions, MEETING_AI_LIMITS.items, MEETING_AI_LIMITS.itemChars);
  }
  const rawTasks = Array.isArray(o.taskProposals) ? o.taskProposals : Array.isArray(o.tasks) ? o.tasks : null;
  if (rawTasks) {
    const seen = new Set<string>();
    const tasks: MeetingAiTaskProposal[] = [];
    for (const t of rawTasks) {
      const item = (typeof t === 'string' ? { title: t } : t) as Record<string, unknown> | null;
      if (!item || typeof item !== 'object') continue;
      let title = clip(item.title, MEETING_AI_LIMITS.titleChars);
      let dueHint = clip(item.dueHint ?? item.due, 200) || null;
      // „Makiety mobile (do wtorku)” → tytuł bez nawiasu, termin z nawiasu (gdy dostawca nie podał go osobno)
      const due = DUE_IN_TITLE.exec(title);
      if (due && !dueHint) {
        dueHint = (due[1] ?? '').trim();
        title = title.slice(0, due.index).trim();
      }
      const key = meetingDecisionKey(title);
      if (!title || !key || seen.has(key)) continue;
      seen.add(key);
      tasks.push({
        title,
        description: clip(item.description, MEETING_AI_LIMITS.itemChars) || null,
        assigneeHint: clip(item.assigneeHint ?? item.assignee, 200) || null,
        dueHint,
      });
      if (tasks.length >= MEETING_AI_LIMITS.proposals) break;
    }
    out.taskProposals = tasks;
  }
  return Object.keys(out).length > 0 ? out : null;
}

/** Wynik AI zapisywany przez usługę (po `coerceMeetingAiResult` i uzupełnieniu brakujących pól). */
export const meetingAiResultSchema = z.object({
  summary: z.string().max(MEETING_AI_LIMITS.summaryChars),
  decisions: z.array(z.string().min(1).max(MEETING_AI_LIMITS.itemChars)).max(MEETING_AI_LIMITS.items),
  questions: z.array(z.string().min(1).max(MEETING_AI_LIMITS.itemChars)).max(MEETING_AI_LIMITS.items),
  taskProposals: z
    .array(
      z.object({
        title: z.string().min(1).max(MEETING_AI_LIMITS.titleChars),
        description: z.string().max(MEETING_AI_LIMITS.itemChars).nullable(),
        assigneeHint: z.string().max(200).nullable(),
        dueHint: z.string().max(200).nullable(),
      }),
    )
    .max(MEETING_AI_LIMITS.proposals),
});

// ── Termin z podpowiedzi („do wtorku”, „7.10”) ──────────────────────────────

function plain(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/ł/g, 'l');
}

const MONTHS: Record<string, number> = {
  stycznia: 1,
  lutego: 2,
  marca: 3,
  kwietnia: 4,
  maja: 5,
  czerwca: 6,
  lipca: 7,
  sierpnia: 8,
  wrzesnia: 9,
  pazdziernika: 10,
  listopada: 11,
  grudnia: 12,
};

/** Dzień tygodnia (0 = niedziela) z form „wtorek / wtorku / we wtorek” (bez polskich znaków). */
const WEEKDAYS: [RegExp, number][] = [
  [/\bponiedzial(ek|ku)\b/, 1],
  [/\bwtor(ek|ku)\b/, 2],
  [/\bsrod(a|e|y)\b/, 3],
  [/\bczwart(ek|ku)\b/, 4],
  [/\bpiat(ek|ku)\b/, 5],
  [/\bsobot(a|e|y)\b/, 6],
  [/\bniedziel(a|e|i)\b/, 0],
];

function weekdayOf(date: LocalDate): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

function addDays(date: LocalDate, days: number): LocalDate {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

function validDate(y: number, m: number, d: number): LocalDate | null {
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const iso = `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  return new Date(`${iso}T00:00:00Z`).toISOString().slice(0, 10) === iso ? iso : null;
}

/** Najbliższa data dzień.miesiąc nie wcześniej niż `base` (bez roku: ten albo następny rok). */
function dayMonth(base: LocalDate, d: number, m: number, year?: number): LocalDate | null {
  if (year) return validDate(year, m, d);
  const y = Number(base.slice(0, 4));
  const same = validDate(y, m, d);
  if (same && same >= base) return same;
  return validDate(y + 1, m, d);
}

/**
 * Termin z podpowiedzi AI względem daty spotkania (`base`, Europe/Warsaw). Rozpoznaje: „2026-10-07”, „7.10”,
 * „7 października”, dni tygodnia („do wtorku”, „we wtorek” = najbliższy po dniu spotkania), „jutro”, „pojutrze”,
 * „za 3 dni”, „za tydzień”. Kilka różnych dat albo nic rozpoznanego = `null` (termin pusty, nie zgadujemy).
 */
export function resolveDueHint(hint: string | null | undefined, base: LocalDate): LocalDate | null {
  if (!hint) return null;
  const text = plain(hint);
  const found = new Set<LocalDate>();
  const add = (d: LocalDate | null) => {
    if (d) found.add(d);
  };
  let rest = text;
  for (const m of text.matchAll(/\b(\d{4})-(\d{2})-(\d{2})\b/g)) {
    add(validDate(Number(m[1]), Number(m[2]), Number(m[3])));
    rest = rest.replace(m[0], ' ');
  }
  for (const m of rest.matchAll(/\b(\d{1,2})\.(\d{1,2})(?:\.(\d{4}))?\b/g)) {
    add(dayMonth(base, Number(m[1]), Number(m[2]), m[3] ? Number(m[3]) : undefined));
  }
  for (const m of rest.matchAll(/\b(\d{1,2})\s+([a-z]+)\b/g)) {
    const month = MONTHS[m[2] ?? ''];
    if (month) add(dayMonth(base, Number(m[1]), month));
  }
  if (/\bpojutrze\b/.test(text)) add(addDays(base, 2));
  else if (/\bjutr(o|a)\b/.test(text)) add(addDays(base, 1));
  if (/\b(dzis|dzisiaj)\b/.test(text)) add(base);
  const inDays = /\bza\s+(\d{1,3})\s+dni\b/.exec(text);
  if (inDays) add(addDays(base, Number(inDays[1])));
  if (/\bza\s+tydzien\b/.test(text)) add(addDays(base, 7));
  const inWeeks = /\bza\s+(\d{1,2})\s+tygodni(e)?\b/.exec(text);
  if (inWeeks) add(addDays(base, 7 * Number(inWeeks[1])));
  const baseDay = weekdayOf(base);
  for (const [re, day] of WEEKDAYS) {
    if (re.test(text)) add(addDays(base, (day - baseDay + 7) % 7 || 7));
  }
  return found.size === 1 ? ([...found][0] ?? null) : null;
}

// ── Transkrypcja: plik .txt / .vtt / .srt i wyświetlanie ────────────────────

const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&nbsp;': ' ',
  '&quot;': '"',
  '&#39;': "'",
};

/** „Imię: tekst” (osoba = najwyżej 4 słowa przed dwukropkiem). */
const SPEAKER_RE = /^([\p{L}][\p{L}\p{M} .'-]{0,40}?):\s+(.+)$/u;

/**
 * Tekst transkrypcji z pliku (po stronie przeglądarki): usuwa nagłówek WEBVTT, bloki NOTE / STYLE / REGION,
 * numery i znaczniki czasu napisów (VTT i SRT), zamienia `<v Imię>tekst` na „Imię: tekst”, usuwa inne znaczniki,
 * łączy kolejne fragmenty tej samej osoby i pomija powtórzone linie. Zwykły tekst (.txt) zostaje bez zmian
 * (poza końcami linii i pustymi liniami na końcach).
 */
export function cleanTranscript(text: string, filename?: string): string {
  const source = text.replace(/^﻿/, '').replace(/\r\n?/g, '\n');
  const ext = filename?.toLowerCase().split('.').pop();
  const isSubtitles =
    ext === 'vtt' || ext === 'srt' || /^WEBVTT/.test(source) || /\d+:\d{2}[.,]\d{3}\s*-->/.test(source);
  if (!isSubtitles) return source.replace(/[ \t]+$/gm, '').trim();

  const lines = source.split('\n');
  const out: { speaker: string | null; text: string }[] = [];
  let skipBlock = false;
  for (let i = 0; i < lines.length; i++) {
    const line = (lines[i] ?? '').trim();
    if (line === '') {
      skipBlock = false;
      continue;
    }
    if (skipBlock) continue;
    if (/^WEBVTT\b/.test(line) || /^(NOTE|STYLE|REGION)\b/.test(line)) {
      skipBlock = true;
      continue;
    }
    if (line.includes('-->')) continue;
    // numer napisu (SRT) albo identyfikator wpisu VTT: linia tuż przed znacznikiem czasu
    if (/^\d+$/.test(line) || (lines[i + 1] ?? '').includes('-->')) continue;
    let speaker: string | null = null;
    let body = line;
    const voice = /^<v(?:\.[^\s>]*)?\s+([^>]+)>/i.exec(body);
    if (voice) {
      speaker = (voice[1] ?? '').trim() || null;
      body = body.slice(voice[0].length);
    }
    body = body
      .replace(/<[^>]*>/g, '')
      .replace(/&(amp|lt|gt|nbsp|quot|#39);/g, (e) => ENTITIES[e] ?? e)
      .replace(/\s+/g, ' ')
      .trim();
    if (!speaker) {
      const named = SPEAKER_RE.exec(body);
      if (named && (named[1] ?? '').trim().split(/\s+/).length <= 4) {
        speaker = (named[1] ?? '').trim();
        body = (named[2] ?? '').trim();
      }
    }
    if (!body) continue;
    const prev = out[out.length - 1];
    if (prev && prev.speaker === speaker) {
      if (prev.text === body || prev.text.endsWith(` ${body}`)) continue;
      prev.text = `${prev.text} ${body}`;
      continue;
    }
    out.push({ speaker, text: body });
  }
  return out.map((l) => (l.speaker ? `${l.speaker}: ${l.text}` : l.text)).join('\n');
}

export interface TranscriptLine {
  speaker: string | null;
  /** „16:42” (gdy podano przy osobie) */
  time: string | null;
  text: string;
}

/** Linie transkrypcji do wyświetlenia (Figma 05: osoba, godzina, tekst). „Piotr 16:42: tekst”, „Piotr: tekst”. */
export function parseTranscriptLines(text: string | null | undefined): TranscriptLine[] {
  if (!text) return [];
  const out: TranscriptLine[] = [];
  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const timed = /^([\p{L}][\p{L}\p{M} .'-]{0,40}?)\s+\[?(\d{1,2}:\d{2})(?::\d{2})?\]?\s*[:–-]\s*(.+)$/u.exec(line);
    if (timed) {
      out.push({ speaker: (timed[1] ?? '').trim(), time: timed[2] ?? null, text: (timed[3] ?? '').trim() });
      continue;
    }
    const named = SPEAKER_RE.exec(line);
    if (named && (named[1] ?? '').trim().split(/\s+/).length <= 4) {
      out.push({ speaker: (named[1] ?? '').trim(), time: null, text: (named[2] ?? '').trim() });
      continue;
    }
    out.push({ speaker: null, time: null, text: line });
  }
  return out;
}
